import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, Ban, CheckCircle2, Gavel, History, RefreshCcw, Shield } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  DataTable,
  SearchInput,
  SectionCard,
  type TableColumn,
  Tabs,
} from "../../../../../components/ui";
import { trpc, type RouterOutputs } from "../../../../../lib/trpc";
import { cn } from "../../../../../lib/utils";

export const Route = createFileRoute("/app/clubs/$slug/admin/moderation")({
  validateSearch: (search: Record<string, unknown>) => ({
    userId:
      typeof search.userId === "string" && search.userId.length > 0 ? search.userId : undefined,
    username:
      typeof search.username === "string" && search.username.trim().length > 0
        ? search.username.trim()
        : undefined,
  }),
  component: ClubAdminModerationPage,
});

function ageColor(dateValue: Date | string) {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "text-surface-500";
  const hoursAgo = (Date.now() - date.getTime()) / (1000 * 60 * 60);
  if (hoursAgo >= 48) return "text-error-400 font-semibold";
  if (hoursAgo >= 24) return "text-error-400/70 font-medium";
  return "text-surface-500";
}

function ClubAdminModerationPage() {
  const { slug } = Route.useParams();
  const { userId: searchUserId, username: searchUsername } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: club } = trpc.club.get.useQuery({ slug });
  const [search, setSearch] = useState(searchUsername ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(searchUserId ?? null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSelectedUserId(searchUserId ?? null);
  }, [searchUserId]);

  useEffect(() => {
    if (!searchUsername) return;
    setSearch(searchUsername);
  }, [searchUsername]);

  const handleSelectMember = (userId: string, username: string) => {
    setSelectedUserId(userId);
    void navigate({
      search: { userId, username },
      replace: true,
    });
  };

  const clearSelection = () => {
    setSelectedUserId(null);
    void navigate({
      search: { userId: undefined, username: undefined },
      replace: true,
    });
  };

  const membersQuery = trpc.clubMember.list.useQuery(
    {
      clubId: club?.id ?? "",
      limit: 8,
      page: 1,
      search: debouncedSearch || undefined,
      sortBy: "username",
      sortDir: "asc",
    },
    {
      enabled: !!club?.id && debouncedSearch.length >= 2,
    },
  );
  const feedQuery = trpc.dispute.moderationFeed.useQuery(
    { clubId: club?.id ?? "" },
    { enabled: !!club?.id },
  );
  const playerHistoryQuery = trpc.dispute.getPlayerHistory.useQuery(
    { clubId: club?.id ?? "", userId: selectedUserId ?? "" },
    { enabled: !!club?.id && !!selectedUserId },
  );

  const selectedMember = useMemo(
    () => membersQuery.data?.items.find((item) => item.userId === selectedUserId) ?? null,
    [membersQuery.data?.items, selectedUserId],
  );

  if (feedQuery.isLoading) {
    return (
      <div className="space-y-4">
        <SectionCard title="Unclaimed Oldest">
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-lg bg-surface-800/40 animate-pulse" />
            ))}
          </div>
        </SectionCard>
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard title="Recent Resolutions">
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-14 rounded-lg bg-surface-800/40 animate-pulse" />
              ))}
            </div>
          </SectionCard>
          <SectionCard title="Recent Corrections">
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-14 rounded-lg bg-surface-800/40 animate-pulse" />
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    );
  }

  if (feedQuery.error || !feedQuery.data) {
    return (
      <SectionCard title="Moderation Queue">
        <div className="rounded-lg border border-error-500/30 bg-error-500/10 p-4 text-sm text-error-300">
          {feedQuery.error?.message || "Failed to load moderation feed."}
        </div>
      </SectionCard>
    );
  }

  const feed = feedQuery.data;

  return (
    <div className="space-y-4">
      <SectionCard title="Player Moderation History">
        <div className="space-y-3">
          <SearchInput
            value={search}
            onChange={(event) => {
              const next = event.target.value;
              setSearch(next);
              if (selectedUserId) {
                clearSelection();
              }
            }}
            onClear={() => {
              setSearch("");
              clearSelection();
            }}
            loading={membersQuery.isFetching}
            placeholder="Search club members by username..."
          />

          {debouncedSearch.length > 0 && debouncedSearch.length < 2 ? (
            <p className="text-xs text-surface-500">
              Enter at least 2 characters to search members.
            </p>
          ) : null}

          {debouncedSearch.length >= 2 ? (
            membersQuery.isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="h-11 rounded-lg bg-surface-800/40 animate-pulse" />
                ))}
              </div>
            ) : membersQuery.data?.items.length ? (
              <div className="rounded-lg border border-surface-700/50 divide-y divide-separator overflow-hidden">
                {membersQuery.data.items.map((member) => (
                  <button
                    key={member.userId}
                    type="button"
                    onClick={() => handleSelectMember(member.userId, member.username)}
                    className={cn(
                      "w-full px-3 py-2 text-left flex items-center gap-3 hover:bg-surface-800/50 transition-colors",
                      selectedUserId === member.userId && "bg-brand-500/10",
                    )}
                  >
                    <Avatar
                      size="sm"
                      variant="rounded"
                      src={member.avatarUrl}
                      initials={member.username.charAt(0)}
                      alt={member.username}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-100 truncate">
                        {member.username}
                      </p>
                    </div>
                    <Badge variant="subtle" color={member.status === "offline" ? "gray" : "green"}>
                      {member.status === "offline" ? "Offline" : "Online"}
                    </Badge>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-surface-500">No club members match that search.</p>
            )
          ) : null}

          {playerHistoryQuery.isLoading ? (
            <div className="space-y-2 pt-2">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-16 rounded-lg bg-surface-800/40 animate-pulse" />
              ))}
            </div>
          ) : playerHistoryQuery.error ? (
            <div className="rounded-lg border border-error-500/30 bg-error-500/10 p-3 text-sm text-error-300">
              {playerHistoryQuery.error.message || "Failed to load player moderation history."}
            </div>
          ) : playerHistoryQuery.data ? (
            <PlayerModerationPanel data={playerHistoryQuery.data} slug={slug} />
          ) : (
            <div className="rounded-lg border border-surface-700/40 bg-surface-900/30 px-4 py-3 text-sm text-surface-400">
              {selectedMember
                ? `Select "${selectedMember.username}" to load moderation details.`
                : "Search and select a player to view moderation history."}
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        header={
          <div className="flex items-center justify-between gap-2 px-4 py-3">
            <div className="flex items-center gap-2">
              <Gavel className="size-4 text-brand-400" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-white">
                Unclaimed Oldest
              </h2>
              <Badge variant="subtle" color={feed.unclaimedOldest.length > 0 ? "yellow" : "gray"}>
                {feed.unclaimedOldest.length}
              </Badge>
            </div>
            <button
              type="button"
              onClick={() => feedQuery.refetch()}
              className="flex items-center justify-center size-7 rounded-lg text-surface-400 hover:text-brand-400 hover:bg-surface-700/40 transition-colors"
              title="Refresh feed"
            >
              <RefreshCcw
                className={cn(
                  "size-3.5",
                  (feedQuery.isFetching || feedQuery.isLoading) && "animate-spin",
                )}
              />
            </button>
          </div>
        }
      >
        {feed.unclaimedOldest.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="flex items-center justify-center size-10 rounded-xl bg-surface-800 border border-surface-700/50 mb-3">
              <Gavel className="size-5 text-surface-500" />
            </div>
            <p className="text-sm font-medium text-surface-300">All clear</p>
            <p className="mt-1 text-xs text-surface-500">
              No unclaimed disputes waiting for attention.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {feed.unclaimedOldest.map((item) => (
              <Link
                key={item.disputeId}
                to="/app/clubs/$slug/admin/disputes"
                params={{ slug }}
                search={{
                  status: "all_unresolved",
                  sort: "oldest",
                  claim: "all",
                  claimer: "all",
                  dispute: item.disputeId,
                }}
                className={cn(
                  "block rounded-lg border border-surface-800/60 bg-surface-900/30 p-3 hover:bg-surface-800/60 transition-colors border-l-4",
                  item.status === "open" ? "border-l-yellow-400/70" : "border-l-blue-400/70",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="subtle" color={item.status === "open" ? "yellow" : "blue"}>
                    {formatStatus(item.status)}
                  </Badge>
                  <span className="text-xs text-surface-500">#{item.disputeId.slice(-8)}</span>
                </div>
                <p className="mt-2 text-sm text-surface-100 line-clamp-2">{item.reason}</p>
                <p className={cn("mt-1 text-xs", ageColor(item.createdAt))}>
                  {item.reporterUsername || item.reporterId} • {formatAgo(item.createdAt)}
                </p>
              </Link>
            ))}
          </div>
        )}
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard
          header={
            <div className="flex items-center gap-2 px-4 py-3">
              <CheckCircle2 className="size-4 text-brand-400" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-white">
                Recent Resolutions
              </h2>
              <Badge variant="subtle" color={feed.recentResolutions.length > 0 ? "green" : "gray"}>
                {feed.recentResolutions.length}
              </Badge>
            </div>
          }
        >
          {feed.recentResolutions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex items-center justify-center size-10 rounded-xl bg-surface-800 border border-surface-700/50 mb-3">
                <CheckCircle2 className="size-5 text-surface-500" />
              </div>
              <p className="text-sm font-medium text-surface-300">No resolutions yet</p>
              <p className="mt-1 text-xs text-surface-500">Resolved disputes will appear here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {feed.recentResolutions.map((item) => (
                <div
                  key={item.disputeId}
                  className="rounded-lg border border-surface-800/60 bg-surface-900/30 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-surface-200">#{item.disputeId.slice(-8)}</span>
                    <Badge variant="subtle" color={resolutionColor(item.resolution)}>
                      {formatResolution(item.resolution)}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-surface-500">
                    {item.resolvedByUsername || item.resolvedById || "Unknown resolver"} •{" "}
                    {item.resolvedAt ? formatAgo(item.resolvedAt) : "unknown"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard
          header={
            <div className="flex items-center gap-2 px-4 py-3">
              <History className="size-4 text-brand-400" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-white">
                Recent Corrections
              </h2>
              <Badge variant="subtle" color={feed.recentCorrections.length > 0 ? "yellow" : "gray"}>
                {feed.recentCorrections.length}
              </Badge>
            </div>
          }
        >
          {feed.recentCorrections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex items-center justify-center size-10 rounded-xl bg-surface-800 border border-surface-700/50 mb-3">
                <History className="size-5 text-surface-500" />
              </div>
              <p className="text-sm font-medium text-surface-300">No corrections recorded</p>
              <p className="mt-1 text-xs text-surface-500">
                Resolution amendments will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {feed.recentCorrections.map((entry) => (
                <div
                  key={entry.auditId}
                  className={cn(
                    "rounded-lg border px-3 py-2",
                    "border-yellow-500/25 bg-yellow-500/10",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-surface-100">#{entry.disputeId.slice(-8)}</span>
                    <span className="text-xs text-yellow-300">
                      {formatResolution(entry.previousResolution)} →{" "}
                      {formatResolution(entry.newResolution)}
                    </span>
                  </div>
                  {entry.correctionReason && (
                    <p className="mt-1 text-xs text-yellow-100/90">{entry.correctionReason}</p>
                  )}
                  <p className="mt-1 text-xs text-yellow-200/80">
                    {entry.changedByUsername} • {formatAgo(entry.createdAt)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function formatStatus(value: string) {
  if (value === "under_review") return "Under Review";
  if (value === "open") return "Open";
  return value;
}

function formatResolution(value: string | null) {
  if (value === "upheld") return "Upheld";
  if (value === "overturned") return "Overturned";
  if (value === "cancelled") return "Cancelled";
  return "Unknown";
}

function resolutionColor(value: string | null): "gray" | "blue" | "yellow" | "green" | "red" {
  if (value === "upheld") return "green";
  if (value === "overturned") return "blue";
  if (value === "cancelled") return "red";
  return "gray";
}

function formatAgo(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  const deltaSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)}m ago`;
  if (deltaSeconds < 86400) return `${Math.floor(deltaSeconds / 3600)}h ago`;
  return `${Math.floor(deltaSeconds / 86400)}d ago`;
}

type PlayerHistoryData = NonNullable<RouterOutputs["dispute"]["getPlayerHistory"]>;

function PlayerModerationPanel({ data, slug }: { data: PlayerHistoryData; slug: string }) {
  const [activeHistoryTab, setActiveHistoryTab] = useState("disputes");

  const summaryItems = [
    { label: "Disputes Filed", value: String(data.summary.disputesFiled) },
    { label: "In Disputed Matches", value: String(data.summary.disputesAsParticipant) },
    { label: "Warnings", value: String(data.summary.totalWarnings) },
    {
      label: "Credibility Score",
      value:
        data.summary.credibilityScore === null
          ? "N/A"
          : `${data.summary.credibilityScore.toString()}%`,
    },
    { label: "Active Club Bans", value: String(data.summary.activeClubBans) },
    { label: "Total Club Bans", value: String(data.summary.totalClubBans) },
    { label: "Active Queue Bans", value: String(data.summary.activeQueueBans) },
    { label: "Total Queue Bans", value: String(data.summary.totalQueueBans) },
  ] as const;

  const disputeColumns: TableColumn<PlayerHistoryData["disputes"][number]>[] = [
    {
      key: "createdAt",
      header: "Date",
      sortable: true,
      getSortValue: (row) => row.createdAt,
      render: (row) => (
        <div className="space-y-0.5">
          <p className="text-sm text-surface-100">{formatDate(row.createdAt)}</p>
          <p className="text-xs text-surface-500">{formatAgo(row.createdAt)}</p>
        </div>
      ),
    },
    {
      key: "matchId",
      header: "Match",
      render: (row) => (
        <Link
          to="/app/clubs/$slug/admin/disputes"
          params={{ slug }}
          search={{
            status: row.resolution || row.withdrawnAt ? "resolved" : "all_unresolved",
            sort: "oldest",
            claim: "all",
            claimer: "all",
            dispute: row.disputeId,
          }}
          className="text-sm text-brand-300 hover:text-brand-200 hover:underline"
        >
          #{row.matchId.slice(-8)}
        </Link>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (row) => (
        <span className="text-sm text-surface-300">{formatCategory(row.category)}</span>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (row) => (
        <Badge variant="subtle" color={row.role === "reporter" ? "blue" : "gray"}>
          {row.role === "reporter" ? "Reporter" : "Participant"}
        </Badge>
      ),
    },
    {
      key: "resolution",
      header: "Resolution",
      render: (row) => {
        const label = row.withdrawnAt ? "Withdrawn" : formatResolution(row.resolution);
        const color = row.withdrawnAt ? "gray" : resolutionColor(row.resolution);
        return (
          <Badge variant="subtle" color={color}>
            {label}
          </Badge>
        );
      },
    },
    {
      key: "resolver",
      header: "Admin",
      render: (row) => (
        <span className="text-sm text-surface-400">
          {row.resolvedByUsername || row.resolvedById || "\u2014"}
        </span>
      ),
    },
  ];

  const banColumns: TableColumn<PlayerHistoryData["queueBans"][number]>[] = [
    {
      key: "createdAt",
      header: "Date",
      sortable: true,
      getSortValue: (row) => row.createdAt,
      render: (row) => (
        <div className="space-y-0.5">
          <p className="text-sm text-surface-100">{formatDate(row.createdAt)}</p>
          <p className="text-xs text-surface-500">{formatAgo(row.createdAt)}</p>
        </div>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      render: (row) => (
        <span className="text-sm text-surface-200">{formatBanReason(row.reason)}</span>
      ),
    },
    {
      key: "durationSeconds",
      header: "Duration",
      sortable: true,
      getSortValue: (row) => row.durationSeconds,
      render: (row) => (
        <span className="text-sm text-surface-300">{formatDuration(row.durationSeconds)}</span>
      ),
    },
    {
      key: "expired",
      header: "Expired",
      render: (row) => (
        <Badge variant="subtle" color={row.expired ? "gray" : "red"}>
          {row.expired ? "Yes" : "No"}
        </Badge>
      ),
    },
  ];

  const clubBanColumns: TableColumn<PlayerHistoryData["clubBans"][number]>[] = [
    {
      key: "createdAt",
      header: "Date",
      sortable: true,
      getSortValue: (row) => row.createdAt,
      render: (row) => (
        <div className="space-y-0.5">
          <p className="text-sm text-surface-100">{formatDate(row.createdAt)}</p>
          <p className="text-xs text-surface-500">{formatAgo(row.createdAt)}</p>
        </div>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      render: (row) => <span className="text-sm text-surface-200">{row.reason || "\u2014"}</span>,
    },
    {
      key: "expiresAt",
      header: "Expires",
      render: (row) => (
        <span className="text-sm text-surface-300">
          {row.expiresAt ? formatDate(row.expiresAt) : "Permanent"}
        </span>
      ),
    },
    {
      key: "expired",
      header: "Expired",
      render: (row) => (
        <Badge variant="subtle" color={row.expired ? "gray" : "red"}>
          {row.expired ? "Yes" : "No"}
        </Badge>
      ),
    },
  ];

  const warningColumns: TableColumn<PlayerHistoryData["warnings"][number]>[] = [
    {
      key: "createdAt",
      header: "Date",
      sortable: true,
      getSortValue: (row) => row.createdAt,
      render: (row) => (
        <div className="space-y-0.5">
          <p className="text-sm text-surface-100">{formatDate(row.createdAt)}</p>
          <p className="text-xs text-surface-500">{formatAgo(row.createdAt)}</p>
        </div>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      render: (row) => <span className="text-sm text-surface-200">{row.reason}</span>,
    },
    {
      key: "warnedBy",
      header: "Issued By",
      render: (row) => (
        <span className="text-sm text-surface-300">{row.warnedByUsername || row.warnedById}</span>
      ),
    },
    {
      key: "matchId",
      header: "Match",
      render: (row) => (
        <Link
          to="/app/match/$shortId"
          params={{ shortId: row.matchId }}
          className="text-sm text-brand-300 hover:text-brand-200 hover:underline"
        >
          #{row.matchId.slice(-8)}
        </Link>
      ),
    },
    {
      key: "disputeId",
      header: "Dispute",
      render: (row) =>
        row.disputeId ? (
          <Link
            to="/app/clubs/$slug/admin/disputes"
            params={{ slug }}
            search={{
              status: "all_unresolved",
              sort: "oldest",
              claim: "all",
              claimer: "all",
              dispute: row.disputeId,
            }}
            className="text-sm text-brand-300 hover:text-brand-200 hover:underline"
          >
            #{row.disputeId.slice(-8)}
          </Link>
        ) : (
          <span className="text-sm text-surface-500">&mdash;</span>
        ),
    },
  ];

  return (
    <div className="space-y-4 pt-1">
      <div className="flex items-center gap-3 rounded-lg border border-surface-700/50 bg-surface-875 p-3">
        <Avatar
          size="md"
          variant="rounded"
          src={data.player.avatarUrl}
          initials={data.player.username.charAt(0)}
          alt={data.player.username}
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-surface-100 truncate">{data.player.username}</p>
          <p className="text-xs text-surface-500">Player ID: {data.player.id.slice(-8)}</p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Link
            to="/app/clubs/$slug/matches"
            params={{ slug }}
            search={{
              clubGameId: undefined,
              queueId: undefined,
              userId: data.player.id,
              me: false,
              period: undefined,
            }}
            className="rounded-md border border-surface-700/60 bg-surface-800/40 px-2.5 py-1 text-xs font-semibold text-surface-200 hover:text-brand-300 hover:border-brand-500/40 transition-colors"
          >
            View Matches
          </Link>
          <Link
            to="/app/users/$slug"
            params={{ slug: data.player.id }}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md border border-surface-700/60 bg-surface-800/40 px-2.5 py-1 text-xs font-semibold text-surface-200 hover:text-brand-300 hover:border-brand-500/40 transition-colors"
          >
            Open Profile
          </Link>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {summaryItems.map((item) => (
          <div
            key={item.label}
            className="rounded-lg border border-surface-700/40 bg-surface-900/30 px-3 py-2"
          >
            <p className="text-xs text-surface-500">{item.label}</p>
            <p className="text-lg font-semibold text-surface-100">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        <div className="rounded-lg border border-surface-700/40 bg-surface-900/30 px-3 py-2">
          <p className="text-xs text-surface-500">Upheld</p>
          <p className="text-lg font-semibold text-green-300">
            {data.summary.resolutionBreakdown.upheld}
          </p>
        </div>
        <div className="rounded-lg border border-surface-700/40 bg-surface-900/30 px-3 py-2">
          <p className="text-xs text-surface-500">Overturned</p>
          <p className="text-lg font-semibold text-blue-300">
            {data.summary.resolutionBreakdown.overturned}
          </p>
        </div>
        <div className="rounded-lg border border-surface-700/40 bg-surface-900/30 px-3 py-2">
          <p className="text-xs text-surface-500">Cancelled / Withdrawn</p>
          <p className="text-lg font-semibold text-yellow-300">
            {data.summary.resolutionBreakdown.cancelledOrWithdrawn}
          </p>
        </div>
      </div>

      {data.summary.queueBanReasonBreakdown.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {data.summary.queueBanReasonBreakdown.map((entry) => (
            <Badge key={entry.reason} variant="subtle" color="purple">
              {formatBanReason(entry.reason)}: {entry.count}
            </Badge>
          ))}
        </div>
      ) : null}

      <div className="space-y-3">
        <Tabs
          tabs={[
            {
              id: "disputes",
              label: `Disputes (${data.disputes.length})`,
              icon: <Gavel className="size-4" />,
            },
            {
              id: "queueBans",
              label: `Queue Bans (${data.queueBans.length})`,
              icon: <Shield className="size-4" />,
            },
            {
              id: "clubBans",
              label: `Club Bans (${data.clubBans.length})`,
              icon: <Ban className="size-4" />,
            },
            {
              id: "warnings",
              label: `Warnings (${data.warnings.length})`,
              icon: <AlertTriangle className="size-4" />,
            },
          ]}
          activeTab={activeHistoryTab}
          onChange={setActiveHistoryTab}
        />

        {activeHistoryTab === "disputes" && (
          <DataTable
            columns={disputeColumns}
            data={data.disputes}
            enableSorting
            initialSort={{ key: "createdAt", direction: "desc" }}
            scrollMaxHeight="max-h-96"
            getRowKey={(row) => row.disputeId}
            emptyState={<p className="text-center text-sm text-surface-500">No dispute history.</p>}
          />
        )}

        {activeHistoryTab === "queueBans" && (
          <DataTable
            columns={banColumns}
            data={data.queueBans}
            enableSorting
            initialSort={{ key: "createdAt", direction: "desc" }}
            scrollMaxHeight="max-h-96"
            getRowKey={(row) => row.banId}
            emptyState={
              <p className="text-center text-sm text-surface-500">No queue bans recorded.</p>
            }
          />
        )}

        {activeHistoryTab === "clubBans" && (
          <DataTable
            columns={clubBanColumns}
            data={data.clubBans}
            enableSorting
            initialSort={{ key: "createdAt", direction: "desc" }}
            scrollMaxHeight="max-h-96"
            getRowKey={(row) => row.banId}
            emptyState={
              <p className="text-center text-sm text-surface-500">No club bans recorded.</p>
            }
          />
        )}

        {activeHistoryTab === "warnings" && (
          <DataTable
            columns={warningColumns}
            data={data.warnings}
            enableSorting
            initialSort={{ key: "createdAt", direction: "desc" }}
            scrollMaxHeight="max-h-96"
            getRowKey={(row) => row.warningId}
            emptyState={
              <p className="text-center text-sm text-surface-500">No warnings recorded.</p>
            }
          />
        )}
      </div>
    </div>
  );
}

function formatCategory(value: string) {
  if (value === "wrong_result") return "Wrong Result";
  if (value === "abandonment") return "Abandonment";
  if (value === "technical") return "Technical";
  if (value === "cheating") return "Cheating";
  return "Other";
}

function formatBanReason(value: string) {
  if (value === "no_show") return "No Show";
  if (value === "declined") return "Declined";
  if (value === "abuse") return "Abuse";
  return "Other";
}

function formatDuration(seconds: number) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function formatDate(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
